import { BookOpen, Books, MagnifyingGlass } from "@phosphor-icons/react";
import type { FeaturePalette, Result, ScopeNode, SearchProvider } from "@/shared/components/command-palette/types";
import { getBooks, getBookWithTOC } from "./api/books";

const READING_PARENT: ScopeNode = { id: "reading", parentId: null, title: "Reading" };

const scopes: ScopeNode[] = [
  {
    id: "reading.library",
    parentId: "reading",
    title: "Library",
    icon: Books,
    href: "/reading",
    resolveChildren: async () => {
      try {
        const books = await getBooks(50, 0);
        return books.map((b) => ({
          id: `reading.book.${b.id}`,
          parentId: "reading.library",
          title: b.title,
          icon: BookOpen,
          href: `/reading/${b.id}`,
          resolveChildren: async () => {
            try {
              const toc = await getBookWithTOC(b.id);
              const chapters = (toc as unknown as { chapters?: Array<{ id: string; title: string }> }).chapters ?? [];
              return chapters.map((c) => ({
                id: `reading.book.${b.id}.ch.${c.id}`,
                parentId: `reading.book.${b.id}`,
                title: c.title,
                icon: BookOpen,
                href: `/reading/${b.id}#chapter-${c.id}`,
              }));
            } catch {
              return [];
            }
          },
        }));
      } catch {
        return [];
      }
    },
  },
  {
    id: "reading.catalog",
    parentId: "reading",
    title: "Search the catalog",
    icon: MagnifyingGlass,
    href: "/reading?tab=discover",
    keywords: ["download", "external", "books", "acquire", "bookbooster"],
  },
  {
    id: "reading.settings",
    parentId: "reading",
    title: "Reading settings",
    icon: MagnifyingGlass,
    href: "/reading?tab=settings",
    keywords: ["quiz", "rabbit hole", "model", "configure", "preferences"],
  },
];

const booksProvider: SearchProvider = {
  id: "books",
  label: "Books",
  icon: BookOpen,
  prefix: "@books",
  async search(q, signal) {
    if (!q) return [];
    try {
      const books = await getBooks(50, 0);
      if (signal.aborted) return [];
      const lower = q.toLowerCase();
      return books
        .filter(
          (b) =>
            b.title.toLowerCase().includes(lower) ||
            (b.author ?? "").toLowerCase().includes(lower)
        )
        .slice(0, 6)
        .map<Result>((b) => ({
          id: `book:${b.id}`,
          title: b.title,
          subtitle: b.author ?? undefined,
          icon: BookOpen,
          scopeChain: [READING_PARENT],
          score: 45,
          onSelect: (ctx) => {
            ctx.router.push(`/reading/${b.id}`);
            ctx.close();
          },
        }));
    } catch {
      return [];
    }
  },
};

export const readingPalette: FeaturePalette = {
  scopes,
  searchProviders: [booksProvider],
};
